import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

export default function DataRequest() {
  const [requestType, setRequestType] = useState<'access' | 'deletion' | 'portability' | 'rectification'>('access');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Store data request in database
      const { error: dbError } = await supabase
        .from('data_requests')
        .insert({
          request_type: requestType,
          email,
          full_name: fullName,
          details,
          status: 'pending',
          created_at: new Date().toISOString()
        });

      if (dbError) throw dbError;

      setSuccess(true);
      setEmail('');
      setFullName('');
      setDetails('');
    } catch (err: any) {
      setError('Failed to submit request. Please try again or contact us directly at privacy@movetoireland.us');
      console.error('Data request error:', err);
    } finally {
      setLoading(false);
    }
  };

  const requestTypes = [
    {
      value: 'access',
      title: 'Access My Data',
      description: 'Request a copy of all personal data we hold about you',
      icon: 'ri-file-download-line'
    },
    {
      value: 'deletion',
      title: 'Delete My Data',
      description: 'Request permanent deletion of your personal data (Right to be Forgotten)',
      icon: 'ri-delete-bin-line'
    },
    {
      value: 'portability',
      title: 'Data Portability',
      description: 'Receive your data in a structured, machine-readable format',
      icon: 'ri-file-transfer-line'
    },
    {
      value: 'rectification',
      title: 'Correct My Data',
      description: 'Request correction of inaccurate or incomplete personal data',
      icon: 'ri-edit-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />
      
      <div className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-6">
              <i className="ri-shield-user-line text-3xl text-white"></i>
            </div>
            <h1 className="text-5xl font-bold text-[#0A381F] mb-4">Data Privacy Request</h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Exercise your GDPR rights. We'll process your request within 30 days as required by law.
            </p>
          </div>

          {success && (
            <div className="mb-8 p-6 bg-green-50 border-2 border-green-200 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-900 mb-2">Request Submitted Successfully</h3>
                  <p className="text-green-800 text-sm leading-relaxed">
                    We've received your data privacy request. You'll receive a confirmation email shortly, 
                    and we'll process your request within 30 days as required by GDPR.
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-8 p-6 bg-red-50 border-2 border-red-200 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-900 mb-2">Submission Failed</h3>
                  <p className="text-red-800 text-sm leading-relaxed">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#0A381F] mb-6">Select Request Type</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {requestTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setRequestType(type.value as any)}
                  className={`p-6 rounded-xl border-2 transition-all duration-300 text-left cursor-pointer ${
                    requestType === type.value
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-lg flex-shrink-0 ${
                      requestType === type.value ? 'bg-[#D4AF37] text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <i className={`${type.icon} text-2xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#0A381F] mb-1">{type.title}</h3>
                      <p className="text-sm text-gray-600">{type.description}</p>
                    </div>
                    {requestType === type.value && (
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-checkbox-circle-fill text-[#D4AF37] text-2xl"></i>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold text-[#0A381F] mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-base"
                  placeholder="John Doe"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#0A381F] mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-base"
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  We'll use this email to verify your identity and send you the requested information.
                </p>
              </div>

              <div>
                <label htmlFor="details" className="block text-sm font-semibold text-[#0A381F] mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  id="details"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-base resize-none"
                  placeholder="Provide any additional information that will help us process your request..."
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-2">
                  {details.length}/500 characters
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-information-line text-blue-600 text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-blue-900 mb-2">Processing Timeline</h4>
                    <p className="text-blue-800 text-sm leading-relaxed">
                      We will respond to your request within <strong>30 days</strong> as required by GDPR. 
                      In complex cases, we may extend this period by an additional 60 days and will notify you accordingly.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#C19B2F] text-white py-4 rounded-lg font-semibold text-base transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting Request...' : 'Submit Request'}
              </button>
            </form>
          </div>

          <div className="bg-gray-50 rounded-xl p-8">
            <h3 className="text-xl font-bold text-[#0A381F] mb-4">Need Help?</h3>
            <p className="text-gray-700 mb-6">
              If you have questions about your data privacy rights or need assistance with your request, 
              please contact our Data Protection Officer:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A381F] rounded-lg">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-[#0A381F]">privacy@movetoireland.us</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A381F] rounded-lg">
                  <i className="ri-shield-user-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Data Protection Officer</p>
                  <p className="font-semibold text-[#0A381F]">dpo@movetoireland.us</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/privacy" 
                className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/terms" 
                className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <span className="text-gray-400">•</span>
              <Link 
                to="/" 
                className="text-[#0A381F] hover:text-[#D4AF37] font-semibold cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
